/**
 * Tenant resolution utility.
 * Extracts tenant slug from the current hostname (subdomain-based).
 */

/** Reserved non-tenant hosts. Currently only `start` (self-serve signup). */
export type ReservedHost = 'start';

/**
 * Detects a RESERVED, non-tenant host that must be handled BEFORE
 * TenantProvider mounts. `start.kulalearn.com` serves the self-serve tenant
 * signup flow and — unlike `hub` — is NOT a tenant row, so without this guard
 * getTenantSlug() would resolve 'start' as a tenant and fall back to thinkshow.
 *
 * Dev override (mirrors the `?tenant=` pattern in getTenantSlug):
 *   - `?host=start` selects the start host on localhost/preview and persists to
 *     localStorage (`dev_reserved_host`) so internal navigation keeps it.
 *   - `?tenant=…` always wins and clears the override, so you can flip back to
 *     a normal tenant.
 */
export function getReservedHost(): ReservedHost | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);

  // ?tenant=… always wins and clears any reserved-host override.
  if (params.get('tenant')) {
    try { localStorage.removeItem('dev_reserved_host'); } catch { /* ignore */ }
    return null;
  }

  // ?host=start dev override — persist like dev_tenant_slug.
  if (params.get('host') === 'start') {
    try { localStorage.setItem('dev_reserved_host', 'start'); } catch { /* ignore */ }
    return 'start';
  }

  // Production: first hostname label is the reserved host.
  if (window.location.hostname.split('.')[0] === 'start') return 'start';

  // Persisted dev override (localhost/preview, where the hostname carries no
  // distinguishing label).
  try {
    if (localStorage.getItem('dev_reserved_host') === 'start') return 'start';
  } catch { /* ignore */ }

  return null;
}

export function getTenantSlug(): string {
  const hostname = window.location.hostname;

  // ?tenant=<slug> query param always wins — useful on preview deploys.
  const qp = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('tenant')
    : null;
  if (qp) {
    try { localStorage.setItem('dev_tenant_slug', qp); } catch { /* ignore */ }
    return qp;
  }

  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  // Vercel preview deploys (`<project>-<hash>-<scope>.vercel.app`) don't carry
  // a tenant subdomain — fall through to the same override chain as localhost
  // so reviewers can pick a tenant via `?tenant=…`, localStorage, or env.
  const isVercelPreview = hostname.endsWith('.vercel.app');

  if (isVercelPreview) {
    // Preview deploys never use VITE_DEFAULT_TENANT (which is set to a
    // production tenant on the Vercel project). Override chain:
    // localStorage → 'luke'.
    const stored = typeof window !== 'undefined' ? localStorage.getItem('dev_tenant_slug') : null;
    return stored || 'luke';
  }

  if (isLocalhost) {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('dev_tenant_slug') : null;
    return stored || import.meta.env.VITE_DEFAULT_TENANT || 'thinkshow';
  }

  // Subdomain-based: tenant-a.lms.example.com → 'tenant-a'
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    return parts[0];
  }

  // Bare domain (lms.example.com with only 2 parts) → 'thinkshow'
  return 'thinkshow';
}
